/**
 * Primitivas visuais compartilhadas pelos modais premium de detalhes.
 * Mantêm cartões, linhas, relacionamentos e canal lateral com o mesmo padrão.
 *
 * RELEASE: 2026-10-09 v1.0.0 — Criação para unificar Tarefas e Ordem de Serviço.
 */
import type { LucideIcon } from 'lucide-react';
import { Eye, EyeOff } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

export function DetailsSidebarShell({ children, mobile = false, className }: { children: ReactNode; mobile?: boolean; className?: string }) {
  return (
    <div className={cn('h-full shrink-0 overflow-y-auto scrollbar-thin', mobile ? 'w-full' : 'w-[360px] border-l border-border/40 bg-muted/30', className)}>
      <div className={cn('space-y-3', mobile ? 'p-0' : 'p-3')}>{children}</div>
    </div>
  );
}

export function DetailsSidebarCard({ title, icon: Icon, children }: { title: string; icon?: LucideIcon; children: ReactNode }) {
  return (
    <section className="rounded-xl border border-border bg-card p-3 shadow-sm">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{title}</h4>
        {Icon && <Icon className="h-3.5 w-3.5 text-muted-foreground" />}
      </div>
      <div className="space-y-1">{children}</div>
    </section>
  );
}

export function DetailsInfoRow({ icon: Icon, label, children }: { icon: LucideIcon; label: string; children?: ReactNode }) {
  return (
    <div className="flex w-full items-center gap-2 py-1.5">
      <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <span className="shrink-0 text-xs text-muted-foreground">{label}</span>
      <div className="ml-auto min-w-0 text-right text-xs font-medium">
        {children || <span className="text-muted-foreground">—</span>}
      </div>
    </div>
  );
}

export function DetailsRelationshipRow({ icon: Icon, label, value, onView, disabled, disabledTooltip }: {
  icon: LucideIcon;
  label: string;
  value: string | null;
  onView?: () => void;
  disabled?: boolean;
  disabledTooltip?: string;
}) {
  const eye = value && disabled ? (
    <Tooltip>
      <TooltipTrigger asChild><span className="shrink-0 p-0.5"><EyeOff className="h-3.5 w-3.5 text-muted-foreground/40" /></span></TooltipTrigger>
      <TooltipContent side="left"><p className="text-xs">{disabledTooltip || 'Sem permissão para visualizar'}</p></TooltipContent>
    </Tooltip>
  ) : value && onView ? (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button type="button" variant="ghost" size="icon" className="h-5 w-5 shrink-0" onClick={onView} aria-label={`Visualizar ${label}`}>
          <Eye className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="left"><p className="text-xs">Visualizar {label.toLowerCase()}</p></TooltipContent>
    </Tooltip>
  ) : null;

  return (
    <div className="flex w-full items-center gap-2 py-1.5">
      <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <span className="shrink-0 text-xs text-muted-foreground">{label}</span>
      <div className="ml-auto flex min-w-0 items-center gap-1.5">
        <span className={cn('max-w-[180px] truncate text-xs', value ? 'font-medium' : 'italic text-muted-foreground')}>{value || 'Não vinculado'}</span>
        {eye}
      </div>
    </div>
  );
}

export function DetailsContentCard({ title, icon: Icon, action, children, className }: {
  title: string;
  icon?: LucideIcon;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('rounded-xl border border-border bg-card shadow-sm', className)}>
      <header className="flex items-center justify-between border-b border-border/50 px-3 py-2.5">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="h-3.5 w-3.5 text-muted-foreground" />}
          <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{title}</h3>
        </div>
        {action}
      </header>
      <div className="p-3">{children}</div>
    </section>
  );
}